'use client';

import { useEffect, useState } from 'react';
import { useQuery } from '@zg-rentals/react-query5';
import { fetchTreatmentsByGuid, type HpTreatmentDto, isControlTreatment } from './hotpadsExperimentClient';
import { getGuidFromBrowser } from './guidCookie';
import { getOverride } from './overrides';
import { EXPERIMENT_OVERRIDES_CHANGED_EVENT } from './queryParams';
import { experimentKeys } from './queryKeys';
import type { ExperimentName } from './registry';

export type TreatmentResult = {
  treatment: string | undefined;
  isControl: boolean;
  isLoading: boolean;
  isOverridden: boolean;
};

function useClientOverride(name: ExperimentName): string | undefined {
  // Lazy initializer reads the cookie synchronously on the first render so the
  // hook returns the override before `useQuery` ever runs. `useTreatment` is
  // `'use client'` so reading `document.cookie` here is safe — the SSR pass
  // never enters this code path.
  const [override, setOverride] = useState<string | undefined>(() =>
    typeof document !== 'undefined' ? getOverride(name) : undefined,
  );

  // Re-read after `processExperimentParamsFromURL` (in `AppBootstrap`) writes
  // or clears cookies. Without this, a hook that mounts before AppBootstrap's
  // useEffect runs would never see overrides set from a fresh `?xp_…` URL on
  // the same page load.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setOverride(getOverride(name));
    window.addEventListener(EXPERIMENT_OVERRIDES_CHANGED_EVENT, handler);
    return () => window.removeEventListener(EXPERIMENT_OVERRIDES_CHANGED_EVENT, handler);
  }, [name]);

  return override;
}

/**
 * Read the treatment for an active experiment. Reads the override cookie first
 * (set by `?xp_<NAME>=variant1` URL params); falls back to a React Query fetch
 * keyed by experiment name. The query runs against
 * `POST /v2/experiment/getTreatmentsByGuid` with the hotpads `guid` cookie as
 * the targeting key.
 *
 * SSR-prefetched treatments arrive through the app's existing `HydrationBoundary`
 * in `providers.tsx`, so a route that calls `prefetchTreatment` server-side
 * will not flash control on hydration.
 */
export function useTreatment(name: ExperimentName): TreatmentResult {
  const override = useClientOverride(name);
  const guid = typeof document !== 'undefined' ? getGuidFromBrowser() : undefined;

  const { data, isLoading } = useQuery<HpTreatmentDto | null, Error>({
    queryKey: experimentKeys.treatment(name, guid),
    queryFn: async () => {
      if (!guid) return null;
      const results = await fetchTreatmentsByGuid({ experiments: [name], userGuid: guid });
      // Case-insensitive match so a backend that ever normalizes featureName
      // doesn't silently bucket every user as control.
      return results.find((r) => r.featureName.toLowerCase() === (name as string).toLowerCase()) ?? null;
    },
    enabled: override === undefined,
    staleTime: Infinity,
    retry: 0,
    meta: {
      tags: ['experiment'],
      context: {
        api: 'hotpads-api',
        details: `fetch treatment ${name}`,
      },
    },
  });

  if (override !== undefined) {
    return {
      treatment: override,
      isControl: isControlTreatment(override),
      isLoading: false,
      isOverridden: true,
    };
  }

  return {
    treatment: data?.treatment,
    isControl: isControlTreatment(data?.treatment),
    isLoading,
    isOverridden: false,
  };
}
